import { motion, AnimatePresence } from 'framer-motion';
import { Scale, Sparkles } from 'lucide-react';
import { MODELS, MODEL_KEYS } from '../lib/models.js';

export function SynthesisPanel({ enabled, onToggle, judgeKey, onJudgeChange, judgePrompt, onJudgePromptChange }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
      className={`panel relative overflow-hidden p-5 ${enabled ? 'ring-1 ring-accent-orange/40' : ''}`}
    >
      {enabled && (
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-accent-orange/[0.06] via-transparent to-accent-cyan/[0.06]" />
      )}
      <div className="relative flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${enabled ? 'bg-accent-orange/15 text-accent-orange-soft' : 'bg-bg-surface2 text-ink-dim'}`}>
            <Scale className="h-4 w-4" />
          </div>
          <div>
            <div className="section-title">
              Synthesis mode
              {enabled && (
                <span className="chip-orange">
                  <Sparkles className="h-3 w-3" /> active
                </span>
              )}
            </div>
            <p className="mt-1 max-w-xl text-xs text-ink-dim">
              A judge agent reads every response, picks the strongest answer (or composes a synthesis), and
              cites which agents contributed which ideas.
            </p>
          </div>
        </div>
        <Toggle enabled={enabled} onChange={onToggle} />
      </div>

      <AnimatePresence initial={false}>
        {enabled && (
          <motion.div
            initial={{ opacity: 0, height: 0, marginTop: 0 }}
            animate={{ opacity: 1, height: 'auto', marginTop: 16 }}
            exit={{ opacity: 0, height: 0, marginTop: 0 }}
            transition={{ duration: 0.3 }}
            className="relative grid gap-3 md:grid-cols-[260px,1fr]"
          >
            <div>
              <label className="label mb-1.5">Judge model</label>
              <select
                value={judgeKey}
                onChange={(e) => onJudgeChange(e.target.value)}
                className="input cursor-pointer"
              >
                {MODEL_KEYS.map((k) => (
                  <option key={k} value={k} className="bg-bg-deep">
                    {MODELS[k].label} · T{MODELS[k].tier}
                  </option>
                ))}
              </select>
              <p className="mt-1 font-mono text-[10px] text-ink-dim">
                ${MODELS[judgeKey]?.inputPer1M}/${MODELS[judgeKey]?.outputPer1M} per 1M tokens
              </p>
            </div>
            <div>
              <label className="label mb-1.5">Judge instructions</label>
              <textarea
                value={judgePrompt}
                onChange={(e) => onJudgePromptChange(e.target.value)}
                rows={3}
                className="input scrollbar-stealth text-sm"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
}

function Toggle({ enabled, onChange }) {
  return (
    <button
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${enabled ? 'bg-gradient-to-r from-accent-orange to-accent-orange-dark' : 'bg-bg-surface2 ring-1 ring-border-strong'}`}
      aria-pressed={enabled}
    >
      <motion.span
        layout
        transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        className={`inline-block h-5 w-5 rounded-full bg-white shadow-md ${enabled ? 'translate-x-6' : 'translate-x-1'}`}
      />
    </button>
  );
}
